import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { StringUtilsHelper } from 'src/app/utils/string-utils-helper';
import { BiVizDashboardResourcesSqlMap } from '../../mopa-resources-detailed/sql-maps/biviz-dashboard-resources-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface MetaSummaryTableDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

export class MetaSummaryTableDataLoaderFactory extends Factory<
    DataLoader,
    MetaSummaryTableDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'MetaSummaryTableDataLoaderFactory';

    getInstance(options: MetaSummaryTableDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const {
                    dateTrunc,
                    splitSelectAsSeparateColumns,
                    splitGroupByTable,
                    where,
                    join,
                } = BiVizDashboardUsersActivity.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options
                );

                const currency =
                    queryContext.settings?.[QueryContextSettingID.CURRENCY] ===
                    'usd'
                        ? 'usd'
                        : 'pln';
                const revenueColumn = `revenue_net_${currency}`;

                // median_energy_spent side query against dashboard_resources — only when every
                // active filter/split is supported by the resources map
                const resourcesSqlMap = BiVizDashboardResourcesSqlMap.sqlMap;
                const isEnergySupported = dsColumns.present.every(
                    (dsCol) => !!resourcesSqlMap.columns[dsCol.id]
                );

                const splitIds = splitGroupByTable
                    ? splitGroupByTable.split(', ')
                    : [];

                let energyCTE = '';
                let energyJoin = '';
                let energySelect =
                    'CAST(NULL AS FLOAT64) AS median_energy_spent';
                if (isEnergySupported) {
                    // Same filters/splits compiled against the resources map via its standard
                    // compiler (same pattern as resources-by-date / revenue-dau). Split columns
                    // are aliased by their ids on both sides, so splitGroupByTable/splitIds
                    // from the users-activity compile apply here too.
                    const resSQL =
                        BiVizDashboardResourcesSqlMap.sqlStatementsCompiler.compileSQLStatements(
                            dsColumns.present,
                            queryContext.settings,
                            options
                        );

                    energyCTE = `
, energy AS (
  -- dashboard_resources is ~10 rows per player-day (player x date x resource/source/change_type),
  -- so a per-player SUM must come first — a median straight over rows would be over fragments of
  -- the daily spend. SUM(energy_outflow) > 0 per player ≡ hopa_user (verified in BQ, 2026-07-06).
  SELECT
    ${StringUtilsHelper.suffixIfNotNull(splitGroupByTable, ',')}
    date,
    APPROX_QUANTILES(IF(energy_spent > 0, energy_spent, NULL), 100)[OFFSET(50)] AS median_energy_spent
  FROM (
    SELECT
      ${StringUtilsHelper.suffixIfNotNull(
          resSQL.splitSelectAsSeparateColumns,
          ','
      )}
      ${dateTrunc} AS date,
      player_id,
      SUM(energy_outflow) AS energy_spent
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        resourcesSqlMap.dataset,
        resourcesSqlMap.tableId
    )}\` ds
    ${resSQL.join}
    ${resSQL.where}
    GROUP BY ${StringUtilsHelper.suffixIfNotNull(splitGroupByTable, ',')}date, player_id
  )
  GROUP BY ${StringUtilsHelper.suffixIfNotNull(splitGroupByTable, ',')}date
)`;
                    energyJoin = `LEFT JOIN energy
  ON kpi.date = energy.date${splitIds
      .map(
          (id) => `
  AND kpi.${id} IS NOT DISTINCT FROM energy.${id}`
      )
      .join('')}`;
                    energySelect = 'energy.median_energy_spent';
                }

                // newest first, date as the primary sort field — analysts should
                // see the latest period at the top of the table, grouped by date
                // rather than by split; the Day column header still re-sorts both ways
                const orderBy = `kpi.date DESC${StringUtilsHelper.prefixIfNotNull(
                    splitIds.map((id) => `kpi.${id}`).join(', '),
                    ', '
                )}`;

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH players AS (
-- "Median … by player" under date aggregation: the unit stays THE PLAYER — first SUM each
-- player's activity over the period, then aggregate across players. At day granularity this is 1 row = 1 player-day.
SELECT
  ${dateTrunc} AS date,  
  ${StringUtilsHelper.suffixIfNotNull(splitSelectAsSeparateColumns, ',')}
  player_id,
  -- group membership over the period = member on at least one day (LOGICAL_OR skips NULLs)
  LOGICAL_OR(meta_user) AS meta_user,
  LOGICAL_OR(hopa_user) AS hopa_user,
  LOGICAL_OR(meta_prod_user) AS meta_prod_user,
  LOGICAL_OR(core_loop_user) AS core_loop_user,
  SUM(new_decos_placed) AS new_decos_placed,
  SUM(session_time_min) AS session_time_min,
  SUM(meta_time_min) AS meta_time_min,
  SUM(hopa_time_min) AS hopa_time_min,
  SUM(ctx_total_time_daily_min) AS ctx_total_time_min
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${join}
${where}
GROUP BY ${StringUtilsHelper.suffixIfNotNull(splitGroupByTable, ',')}date, player_id
)

, kpi AS (
SELECT
  date,  
  ${StringUtilsHelper.suffixIfNotNull(splitGroupByTable, ',')}
  COUNT(*) AS dau,
  SAFE_DIVIDE(COUNTIF(meta_user), COUNT(*)) AS meta_share,
  SAFE_DIVIDE(COUNTIF(hopa_user), COUNT(*)) AS hopa_share,
  SAFE_DIVIDE(COUNTIF(meta_prod_user), COUNT(*)) AS meta_productive_share,
  SAFE_DIVIDE(COUNTIF(core_loop_user), COUNT(*)) AS core_loop_share,
  APPROX_QUANTILES(COALESCE(new_decos_placed, 0), 100)[OFFSET(50)] AS median_decos_placed,
  APPROX_QUANTILES(COALESCE(session_time_min, 0), 100)[OFFSET(50)] AS median_time_spent_min,
  APPROX_QUANTILES(COALESCE(meta_time_min, 0), 100)[OFFSET(50)] AS median_meta_time_min,
  -- share per player-period = ratio of the player's summed numerator/denominator (ctx).
  -- NO COALESCE — like META Engagement: median over MEASURED users only, NULLs (no ctx data)
  -- drop out. At day granularity this equals the stored per-day meta/hopa_share_ctx.
  APPROX_QUANTILES(SAFE_DIVIDE(meta_time_min, ctx_total_time_min), 100)[OFFSET(50)] AS median_meta_time_share,
  -- NO COALESCE on HOPA time either (UBI-1270). hopa_time_min is now the RESIDUAL of the client
  -- counter (ctx − META − Shop), so it is NULL exactly when ctx is NULL — i.e. on every build below
  -- 4.0.100. Coalescing that to 0 would report "the player did no HOPA" for whole periods that were
  -- simply never measured, and would put this cell on a different population than the share next to
  -- it. META keeps its COALESCE: there 0 is a real value (the player did not decorate).
  APPROX_QUANTILES(hopa_time_min, 100)[OFFSET(50)] AS median_hopa_time_min,
  APPROX_QUANTILES(SAFE_DIVIDE(hopa_time_min, ctx_total_time_min), 100)[OFFSET(50)] AS median_hopa_time_share,
FROM players
GROUP BY ${StringUtilsHelper.suffixIfNotNull(splitGroupByTable, ',')}date
)

-- Monetization rates use the SALES semantics: weighted daily averages over the period
-- (SUM of daily payers / SUM of daily DAU), exactly like wavg_conversion_rate /
-- wavg_ARPDAU in mopa-sales — NOT unique players per period. This keeps aggregated
-- (week/month) values consistent with the Sales dashboard.
, sales_daily AS (
SELECT
  ${StringUtilsHelper.suffixIfNotNull(splitSelectAsSeparateColumns, ',')}
  ${dateTrunc} AS date,
  COUNT(DISTINCT player_id) AS daily_active_users,
  COUNT(DISTINCT IF(transaction_count > 0, player_id, NULL)) AS daily_payers,
  SUM(${revenueColumn}) AS revenue
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${join}
${where}
GROUP BY ${StringUtilsHelper.suffixIfNotNull(splitGroupByTable, ',')}date, ds.date
)

, sales_agg AS (
SELECT
  ${StringUtilsHelper.suffixIfNotNull(splitGroupByTable, ',')}
  date,
  SUM(daily_active_users) AS sum_daily_active_users,
  SUM(daily_payers) AS sum_daily_payers,
  ROUND(SUM(revenue), 2) AS revenue
FROM sales_daily
GROUP BY ${StringUtilsHelper.suffixIfNotNull(splitGroupByTable, ',')}date
)
${energyCTE}

SELECT
  kpi.*,
  sales_agg.revenue,
  ROUND(SAFE_DIVIDE(sales_agg.revenue, sales_agg.sum_daily_active_users), 4) AS arpdau,
  ROUND(SAFE_DIVIDE(sales_agg.revenue, sales_agg.sum_daily_payers), 4) AS arppdau,
  ROUND(SAFE_DIVIDE(sales_agg.sum_daily_payers, sales_agg.sum_daily_active_users), 4) AS conversion_rate,
  ${energySelect}
FROM kpi
LEFT JOIN sales_agg
  ON kpi.date = sales_agg.date${splitIds
      .map(
          (id) => `
  AND kpi.${id} IS NOT DISTINCT FROM sales_agg.${id}`
      )
      .join('')}
${energyJoin}
ORDER BY ${orderBy}`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
